import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, Eye, Image, Video, Film, X, ZoomIn, Link2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const PAGE_SIZE = 50;

const MediaLibrary = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const { hasAnyResource } = usePermission();
  const canView = hasAnyResource(['media']);
  const sentinelRef = useRef(null);

  const fetchFiles = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/media?page=${pageNum}&limit=${PAGE_SIZE}`);
      const result = await res.json();
      if (result.status === 'success' && Array.isArray(result.data)) {
        setFiles(prev => pageNum === 1 ? result.data : [...prev, ...result.data]);
        setHasMore(result.data.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error('Failed to fetch media:', err);
      notify(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  // Initial load
  useEffect(() => {
    setPage(1);
    setFiles([]);
    setHasMore(true);
    fetchFiles(1);
  }, [fetchFiles]);

  // Load more when sentinel enters viewport
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchFiles(nextPage);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchFiles]);

  // ESC key closes preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setPreviewFile(null);
    };
    if (previewFile) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewFile]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity', file.type.startsWith('video/') ? 'video' : 'wysiwyg');

    try {
      const res = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
      });
      const result = await res.json();
      if (result.status === 'success') {
        notify(t('common.success'), 'success');
        setPage(1);
        setFiles([]);
        setHasMore(true);
        fetchFiles(1);
      } else {
        notify(result.message || t('common.error'), 'error');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      notify(t('common.error'), 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (mediaId) => {
    setDeleting(mediaId);
    try {
      const res = await fetch(`/api/media/delete/${mediaId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await res.json();
      if (result.status === 'success') {
        notify(t('common.success'), 'success');
        setPage(1);
        setFiles([]);
        setHasMore(true);
        fetchFiles(1);
      } else {
        notify(result.message || t('common.error'), 'error');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      notify(t('common.error'), 'error');
    } finally {
      setDeleting(null);
      setDeleteConfirm(null);
    }
  };

  const getFileTypeCategory = (fileType) => {
    if (!fileType) return 'unknown';
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('video/')) return 'video';
    return 'unknown';
  };

  const isVideo = (fileType) => fileType && fileType.startsWith('video/');
  const isImage = (fileType) => fileType && fileType.startsWith('image/');

  const filteredFiles = files.filter(file => {
    if (filter === 'all') return true;
    return getFileTypeCategory(file.file_type) === filter;
  });

  const getEntityBadgeColor = (entity) => {
    const colors = {
      product: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      supplier: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      employee: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      category: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      wysiwyg: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      video: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    };
    return colors[entity] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  };

  const getReferenceLabel = (refs) => {
    if (!refs || refs.length === 0) return null;
    for (const r of refs) {
      if (r.type === 'product_image') return t('media.product_image_ref');
      if (r.type.startsWith('entity_')) return t('media.entity_ref');
      if (r.type === 'swatch') return t('media.swatch_ref');
      if (r.type.startsWith('content_')) return t('media.content_ref');
    }
    return t('media.referenced');
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const truncateFileName = (name, maxLen = 20) => {
    if (name.length <= maxLen) return name;
    const ext = name.split('.').pop();
    const base = name.slice(0, maxLen - ext.length - 3);
    return base + '...' + ext;
  };

  const uploadAccept = 'image/*,video/*';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('media.title')}</h1>
        <label className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors cursor-pointer shadow-sm">
          <Upload size={18} />
          {uploading ? t('media.uploading') : t('media.upload_file')}
          <input
            type="file"
            className="hidden"
            accept={uploadAccept}
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'image', 'video'].map(type => (
          <button
            key={type}
            onClick={() => setFilter(type)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
              filter === type
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {type === 'all' && <Film size={16} />}
            {type === 'image' && <Image size={16} />}
            {type === 'video' && <Video size={16} />}
            {type === 'all' ? t('media.all') : type === 'image' ? t('media.images') : t('media.videos')}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {filteredFiles.length > 0 ? (
          filteredFiles.map(file => (
            <div
              key={file.media_id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden group hover:shadow-md transition-shadow relative"
            >
              {/* Preview area */}
              <div
                className={`aspect-square bg-slate-100 dark:bg-slate-900 flex items-center justify-center overflow-hidden relative group ${canView ? 'cursor-pointer' : ''}`}
                onClick={() => canView && setPreviewFile(file)}
              >
                {isImage(file.file_type) ? (
                  <img
                    src={file.file_path}
                    alt={file.file_path?.split('/').pop() || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : isVideo(file.file_type) ? (
                  <div className="relative w-full h-full">
                    <video
                      src={file.file_path}
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                      controls
                    />
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                      <Video size={12} />
                      <span>VIDEO</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <Film size={40} className="text-slate-300 dark:text-slate-600" />
                  </div>
                )}
                {isImage(file.file_type) && canView && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <ZoomIn size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3 space-y-2">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate" title={file.file_path?.split('/').pop() || ''}>
                  {truncateFileName(file.file_path?.split('/').pop() || '')}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    isImage(file.file_type)
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : isVideo(file.file_type)
                        ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                    {file.file_type?.split('/')[0]?.toUpperCase() || 'FILE'}
                  </span>
                  {file.entity && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getEntityBadgeColor(file.entity)}`}>
                      {file.entity}
                    </span>
                  )}
                  {file.is_referenced && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex items-center gap-1" title={file.references?.map(r => r.detail).join('; ')}>
                      <Link2 size={10} />
                      {getReferenceLabel(file.references) || t('media.referenced')}
                    </span>
                  )}
                </div>
                {file.file_size != null && (
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {formatFileSize(file.file_size)}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                {canView && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                    className="p-2 bg-white/90 dark:bg-slate-700/90 rounded-full text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:text-blue-600 shadow-sm transition-colors"
                    title={t('common.view')}
                  >
                    <Eye size={14} />
                  </button>
                )}
                {file.is_referenced ? (
                <div
                  className="p-2 bg-slate-100 dark:bg-slate-700/60 rounded-full text-slate-400 dark:text-slate-500 shadow-sm cursor-not-allowed"
                  title={t('media.referenced_cannot_delete')}
                >
                  <Trash2 size={14} />
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(file.media_id); }}
                  disabled={deleting === file.media_id}
                  className="p-2 bg-white/90 dark:bg-slate-700/90 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 shadow-sm disabled:opacity-50 transition-colors"
                  title={t('common.delete')}
                >
                  {deleting === file.media_id ? (
                    <span className="inline-block animate-spin">⟳</span>
                  ) : (
                    <Trash2 size={14} />
                  )}
                </button>
              )}
              </div>
            </div>
          ))
        ) : loading ? null : (
          <div className="col-span-full py-20 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
            {filter === 'video' ? (
              <>
                <Video size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-400 dark:text-slate-500">{t('media.no_videos')}</p>
              </>
            ) : (
              <>
                <Image size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-400 dark:text-slate-500">{t('media.no_media_files')}</p>
                <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">{t('media.please_upload')}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Infinite scroll sentinel */}
      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-8">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
              <span className="inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">{t('common.loading') || '加载中...'}</span>
            </div>
          ) : (
            <div className="h-1" />
          )}
        </div>
      )}

      {/* Upload progress indicator */}
      {uploading && (
        <div className="fixed bottom-8 right-8 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg animate-bounce flex items-center gap-2 z-50">
          <Upload size={18} />
          {t('media.uploading')}
        </div>
      )}

      {/* Preview lightbox */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewFile(null)}
          onKeyDown={(e) => e.key === 'Escape' && setPreviewFile(null)}
          tabIndex={0}
        >
          <button
            onClick={() => setPreviewFile(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
          >
            <X size={24} />
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {isImage(previewFile.file_type) ? (
              <img
                src={previewFile.file_path}
                alt={previewFile.file_path?.split('/').pop() || ''}
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
              />
            ) : isVideo(previewFile.file_type) ? (
              <video
                src={previewFile.file_path}
                className="max-w-full max-h-[75vh] rounded-lg shadow-2xl"
                controls
                autoPlay
              />
            ) : (
              <div className="flex items-center justify-center w-64 h-64 bg-slate-800 rounded-lg">
                <Film size={64} className="text-slate-400" />
              </div>
            )}

            <div className="mt-3 flex items-center gap-4 text-sm text-white/70">
              <span className="bg-white/10 px-3 py-1 rounded-full">
                {previewFile.file_type}
              </span>
              {previewFile.file_size != null && (
                <span>{formatFileSize(previewFile.file_size)}</span>
              )}
              {previewFile.entity && (
                <span>{previewFile.entity}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 w-[400px] max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{t('common.confirm')}</h3>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-400 dark:text-slate-500"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{t('media.delete_confirm')}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting === deleteConfirm}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting === deleteConfirm ? (
                  <>
                    <span className="inline-block animate-spin">⟳</span>
                    {t('media.deleting')}
                  </>
                ) : (
                  t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
